import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import {
  BookOpen, Sparkles, MessageSquareQuote, Calculator, Sigma, Pen,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// P0.3 (Translation Plan): hardcoded `lang === "pt_br"` ternaries replaced
// with i18n keys in `reader.json`. The bilingual Section Type labels now flow
// through the standard react-i18next governance workflow.

// -----------------------------------------------------------------------------
// V11 Chapter Content Renderer
//
// Per Charles's Fix 3 spec (developer response fix3 spec.pdf):
// 5 H2 section types get distinct visual treatments. Bilingual EN + PT-BR
// detection. Calculator deep-link icons on Worked Examples per the
// chapter→calculator mapping.
//
// Sections that don't match any of the 5 special types render as plain
// markdown (the chapter body, sub-sections, etc.).
//
// Pass 14 (v11 rev.2, 2026-04-26): KaTeX rendering wired via remark-math +
// rehype-katex. The rev.2 manuscript ships equations in two delimiter styles:
//   • Native KaTeX:   $...$  (inline)  $$...$$  (display)
//   • Pandoc-extract: \(...\)  (inline)  \[...\]  (display)
// Native dollar-delimited blocks are KaTeX-clean. Pandoc blocks need a small
// pre-render normalisation: convert delimiters AND unescape \\frac → \frac,
// \\ln → \ln, etc. (pandoc double-escapes LaTeX commands inside its
// bracket-delimited blocks). See `_normaliseMath()` below.
// -----------------------------------------------------------------------------

// ── Math-delimiter normalisation ─────────────────────────────────────
// Convert pandoc-style \(..\) / \[..\] math blocks to standard
// $..$ / $$..$$ form, unescaping double-backslashes inside.
//   Input:  "\\[ n = \\\\frac{a}{b} \\]"
//   Output: "$$ n = \\frac{a}{b} $$"
// Native $..$ / $$..$$ blocks are passed through untouched.
function _normaliseMath(md) {
  if (!md) return md;
  // Display math: \[ ... \]  →  $$ ... $$
  md = md.replace(/\\\[([\s\S]+?)\\\]/g, (_m, body) => {
    const cleaned = body.replace(/\\\\([a-zA-Z]+)/g, "\\$1")
                        .replace(/\\\^/g, "^")
                        .replace(/\\\\/g, "\\");
    return "$$" + cleaned + "$$";
  });
  // Inline math: \( ... \)  →  $ ... $
  md = md.replace(/\\\(([\s\S]+?)\\\)/g, (_m, body) => {
    const cleaned = body.replace(/\\\\([a-zA-Z]+)/g, "\\$1")
                        .replace(/\\\^/g, "^")
                        .replace(/\\\\/g, "\\");
    return "$" + cleaned + "$";
  });
  return md;
}

const SECTION_TYPE = {
  ABSTRACT: "abstract",
  KEY_EQUATIONS: "key_equations",
  WORKED_EXAMPLES: "worked_examples",
  SM_CLOSING: "sm_closing",
  TERMINOLOGY: "terminology",
};

// Heading-text → section-type classifier (case-insensitive, EN + PT-BR).
// v11 manuscript headings are typically prefixed with a section number
// (e.g. "9. Worked Examples", "12. Senior-Manager Closing Guidance"); we
// strip the prefix before matching so the classifier works on both
// `## Abstract` (numberless front-matter sections) and `## 9. Worked
// Examples` (numbered methodological-chapter sections).
const HEADING_PATTERNS = [
  { type: SECTION_TYPE.ABSTRACT,        re: /^(abstract|resumo)\s*$/i },
  { type: SECTION_TYPE.KEY_EQUATIONS,   re: /^(key\s+equations|equa[çc][õo]es\s+principais)\s*$/i },
  { type: SECTION_TYPE.WORKED_EXAMPLES, re: /^(worked\s+examples?|exemplos?\s+resolvidos?)\s*$/i },
  { type: SECTION_TYPE.SM_CLOSING,      re: /^(senior[-\s]manager\s+closing\s+guidance|orienta[çc][ãa]o\s+de\s+encerramento\s+para\s+ger[êe]ncia\s+s[êe]nior)\s*$/i },
  { type: SECTION_TYPE.TERMINOLOGY,     re: /^(terminology\s+rules?|regras\s+de\s+terminologia)\s*$/i },
];

function classifyHeading(headingText) {
  if (!headingText) return null;
  // Strip a leading "N." or "N.M." section-number prefix (v11 convention)
  // so "9. Worked Examples" matches the same rule as "Worked Examples".
  // Pandoc emits some numbered headings with a backslash-escaped period
  // (`8\. Key Equations`) — strip the optional `\` before the period too.
  const trimmed = headingText.replace(/^\s*\d+(\.\d+)*\\?\.?\s+/, "").trim();
  for (const p of HEADING_PATTERNS) {
    if (p.re.test(trimmed)) return p.type;
  }
  return null;
}

// Chapter → calculator deep-link mapping (Charles's spec)
const CHAPTER_TO_CALCULATOR = {
  6: "cpk_ppk", 7: "rss_stack", 8: "weibull", 9: "aet", 10: "arrhenius",
  11: "gage_rr", 12: "sigma_dpmo", 16: "supplier_scorecard", 17: "success_run",
};

// -----------------------------------------------------------------------------
// Markdown splitter — splits content at H2 boundaries, returns ordered blocks
// -----------------------------------------------------------------------------

function splitByH2(markdown) {
  if (!markdown) return [];
  const lines = markdown.split("\n");
  const blocks = [];
  let currentHeading = null;
  let currentBody = [];

  const flush = () => {
    if (currentHeading !== null || currentBody.length) {
      blocks.push({
        heading: currentHeading,
        body: currentBody.join("\n").trim(),
        section_type: classifyHeading(currentHeading),
      });
    }
  };

  for (const line of lines) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      flush();
      currentHeading = m[1];
      currentBody = [];
    } else {
      // Strip the manuscript's H1 chapter-title line (which the page header
      // already renders) from the pre-H2 prose so it isn't duplicated.
      if (currentHeading === null && /^\s*#\s+/.test(line)) continue;
      // Also strip any leading line that is just the chapter title repeated
      // as plain text (some v11 docs prepend the title without an H1 marker).
      currentBody.push(line);
    }
  }
  flush();

  // If the first block is heading-less and its body is empty after the H1
  // strip, drop it so the rendered chapter starts with real content.
  if (blocks.length && blocks[0].heading === null && !blocks[0].body) {
    blocks.shift();
  }
  return blocks;
}

// Worked-example sub-splitter (split on H3 boundaries)
function splitByH3(markdown) {
  if (!markdown) return [];
  const lines = markdown.split("\n");
  const examples = [];
  let preface = [];
  let currentTitle = null;
  let currentBody = [];

  const flush = () => {
    if (currentTitle !== null) {
      examples.push({ title: currentTitle, body: currentBody.join("\n").trim() });
    }
  };

  for (const line of lines) {
    const m = line.match(/^###\s+(.+?)\s*$/);
    if (m) {
      if (currentTitle === null && currentBody.length) {
        preface = currentBody;
      } else {
        flush();
      }
      currentTitle = m[1];
      currentBody = [];
    } else {
      currentBody.push(line);
    }
  }
  flush();
  return { preface: preface.join("\n").trim(), examples };
}

// -----------------------------------------------------------------------------
// Per-section renderers
// -----------------------------------------------------------------------------

const PROSE_CLASS =
  "prose prose-invert prose-sm max-w-none " +
  "prose-headings:text-white prose-p:text-slate-300 prose-li:text-slate-300 " +
  "prose-strong:text-white prose-code:text-cyan-300 prose-code:bg-[#0A1628] " +
  "prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none " +
  "prose-table:text-xs prose-th:text-cyan-300 prose-th:border-[#1E3A5F] " +
  "prose-td:border-[#1E3A5F]";

// Shared plugin tuple — remark-math parses $..$ / $$..$$ delimiters into
// math AST nodes; rehype-katex compiles those nodes to KaTeX HTML using
// the bundled katex stylesheet (imported above).
const _REMARK_PLUGINS = [remarkMath];
const _REHYPE_PLUGINS = [[rehypeKatex, { strict: "ignore", trust: false, throwOnError: false }]];

// Drop-in ReactMarkdown wrapper that applies math normalisation + KaTeX.
const MathMarkdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={_REMARK_PLUGINS} rehypePlugins={_REHYPE_PLUGINS}>
    {_normaliseMath(children || "")}
  </ReactMarkdown>
);

const Markdown = ({ children }) => (
  <div className={PROSE_CLASS}>
    <MathMarkdown>{children}</MathMarkdown>
  </div>
);

// Type 1 — Abstract / Resumo
const AbstractBlock = ({ heading, body, lang }) => {
  const { t } = useTranslation("reader");
  const labelText = t("v11.section.abstract");
  // Render first paragraph italic (lead-in); subsequent paragraphs normal
  const paragraphs = body.split(/\n\s*\n/);
  return (
    <section className="bg-slate-800/40 border border-slate-700/40 rounded-lg pl-6 pr-4 py-4 my-6"
             data-testid="v11-section-abstract">
      <div className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-bold mb-3">
        {labelText}
      </div>
      {paragraphs[0] && (
        <div className={`${PROSE_CLASS} prose-p:italic prose-p:leading-snug prose-p:text-slate-200`}>
          <MathMarkdown>{paragraphs[0]}</MathMarkdown>
        </div>
      )}
      {paragraphs.slice(1).join("\n\n") && (
        <div className={`${PROSE_CLASS} prose-p:leading-snug`}>
          <MathMarkdown>{paragraphs.slice(1).join("\n\n")}</MathMarkdown>
        </div>
      )}
    </section>
  );
};

// Type 5 — Key Equations / Equações Principais
const KeyEquationsBlock = ({ heading, body, lang }) => {
  const { t } = useTranslation("reader");
  const labelText = t("v11.section.key_equations");
  return (
    <section className="my-8" data-testid="v11-section-key-equations">
      <div className="flex items-center gap-2 mb-4">
        <Sigma className="w-4 h-4 text-slate-300" />
        <h2 className="text-base font-bold text-white">{labelText}</h2>
      </div>
      {/*
        Render the body, but wrap each numbered equation reference like
        "(6.1)" in a styled callout. The published markdown uses a mix of
        fenced math blocks and prose; we apply uniform vertical breathing
        room and a soft grey background to anything that looks like a
        display equation (a blockquote, fenced math, or a paragraph that
        starts with a `$$ ... $$` block).
      */}
      <div className={`${PROSE_CLASS} ` +
        "prose-blockquote:bg-slate-800/30 prose-blockquote:border-l-2 " +
        "prose-blockquote:border-slate-500 prose-blockquote:py-3 " +
        "prose-blockquote:px-5 prose-blockquote:my-6 " +
        "prose-blockquote:not-italic prose-blockquote:font-mono " +
        "prose-blockquote:text-slate-100 " +
        "prose-pre:bg-slate-800/30 prose-pre:border prose-pre:border-slate-700 " +
        "prose-pre:py-4 prose-pre:px-5 prose-pre:my-6"}>
        <MathMarkdown>{body}</MathMarkdown>
      </div>
    </section>
  );
};

// Type 2 — Worked Examples / Exemplos Resolvidos
const WorkedExamplesBlock = ({ heading, body, lang, chapterNum }) => {
  const { t, i18n } = useTranslation("reader");
  const labelText = t("v11.section.worked_examples");
  const tooltipText = t("v11.calculator.try_live_tooltip");
  const navigate = useNavigate();
  const calcId = CHAPTER_TO_CALCULATOR[chapterNum];

  const { preface, examples } = useMemo(() => splitByH3(body), [body]);

  const openCalc = () => {
    // Canonical PT-BR locale uses /calculadoras; all others (en) use /calculators
    const base = i18n.language === "pt-BR" ? "/calculadoras" : "/calculators";
    navigate(`${base}/${calcId}?canonical=1`);
  };

  return (
    <section className="my-8" data-testid="v11-section-worked-examples">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <h2 className="text-base font-bold text-white">{labelText}</h2>
      </div>
      {preface && <Markdown>{preface}</Markdown>}
      <div className="space-y-4 mt-4">
        {examples.length > 0 ? examples.map((ex, i) => (
          <article
            key={i}
            className="relative bg-[#0D1F38] border border-[#1E3A5F] rounded-lg shadow-md shadow-emerald-900/10 pl-5 pr-4 py-4"
            style={{ borderLeft: "4px solid #10b981" }}
            data-testid={`v11-worked-example-${i + 1}`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold text-white">{ex.title}</h3>
              {calcId && (
                <button
                  onClick={openCalc}
                  title={tooltipText}
                  className="flex-shrink-0 p-1.5 rounded bg-[#1A6BCC]/15 hover:bg-[#1A6BCC]/30 text-[#00C8E0] border border-[#1A6BCC]/40 transition-colors"
                  data-testid={`v11-worked-example-calc-link-${i + 1}`}
                  aria-label={tooltipText}
                >
                  <Calculator className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Markdown>{ex.body}</Markdown>
          </article>
        )) : <Markdown>{body}</Markdown>}
      </div>
    </section>
  );
};

// Type 3 — Senior-Manager Closing Guidance / Orientação de Encerramento…
const SeniorManagerClosingBlock = ({ heading, body, lang }) => {
  const { t } = useTranslation("reader");
  const labelText = t("v11.section.senior_manager_closing");
  return (
    <section className="my-12" data-testid="v11-section-sm-closing">
      <div className="relative bg-gradient-to-br from-emerald-900/40 to-slate-900/60 border border-emerald-700/40 rounded-lg pl-8 pr-6 py-6 mb-8 shadow-lg shadow-emerald-900/20"
           style={{ borderLeft: "4px solid #047857" }}>
        <MessageSquareQuote className="absolute top-4 right-4 w-5 h-5 text-emerald-500/40" />
        <div className="text-[10px] text-emerald-300 uppercase tracking-[0.2em] font-bold mb-3">
          {labelText}
        </div>
        <div className={`${PROSE_CLASS} ` +
          "prose-p:text-slate-100 prose-p:italic prose-p:leading-relaxed " +
          "prose-strong:text-white prose-li:text-slate-100"}>
          <MathMarkdown>{body}</MathMarkdown>
        </div>
      </div>
    </section>
  );
};

// Type 4 — Terminology Rules / Regras de Terminologia
const TerminologyBlock = ({ heading, body, lang }) => {
  const { t } = useTranslation("reader");
  const labelText = t("v11.section.terminology_rules");

  // Detect definition-list pattern. v11 manuscripts use one of:
  //   **Term** — definition           (markdown bold + em-dash)
  //   **Term**: definition            (markdown bold + colon)
  //   Term = definition               (plain "is-defined-as")
  //   Term: definition                (plain colon)
  // Fall back to plain markdown if no rows parse.
  const items = useMemo(() => {
    const out = [];
    const lines = body.split("\n");
    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;
      // Pattern A: **Term** — definition  OR  **Term**: definition
      let m = line.match(/^\*\*(.+?)\*\*\s*[—\-:]\s*(.+)$/);
      if (m) { out.push({ term: m[1].trim(), def: m[2].trim() }); continue; }
      // Pattern B: Term = definition (the v11 manuscript convention)
      // Term must be ≤ 80 chars and not contain another "=" (avoids
      // matching equation lines accidentally).
      m = line.match(/^([^=]{1,80}?)\s+=\s+(.+)$/);
      if (m && !/^\d/.test(m[1])) {
        out.push({ term: m[1].trim(), def: m[2].trim() });
        continue;
      }
      // Pattern C: Term: definition  (only if the term part is 1–4 words)
      m = line.match(/^([A-Z][\w\s/&]{1,40}?):\s+(.+)$/);
      if (m && m[1].split(/\s+/).length <= 5) {
        out.push({ term: m[1].trim(), def: m[2].trim() });
        continue;
      }
    }
    // Require at least 3 rows to confirm this is a definition list (one or
    // two stray matches in a prose paragraph would otherwise misclassify).
    return out.length >= 3 ? out : [];
  }, [body]);

  return (
    <section className="my-8" data-testid="v11-section-terminology">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-4 h-4 text-amber-400" />
        <h2 className="text-base font-bold text-white">{labelText}</h2>
      </div>
      {items.length > 0 ? (
        <dl className="border border-[#1E3A5F]/60 rounded-lg overflow-hidden">
          {items.map(({ term, def }, i) => (
            <div
              key={i}
              className={`flex flex-col sm:flex-row gap-1 sm:gap-4 px-4 py-2.5 ${
                i % 2 === 0 ? "bg-[#0A1628]" : "bg-[#0D1F38]"
              }`}
            >
              <dt className="font-bold text-white text-sm sm:w-1/3 sm:text-base">{term}</dt>
              <dd className="text-slate-300 text-sm sm:w-2/3 leading-snug">{def}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <Markdown>{body}</Markdown>
      )}
    </section>
  );
};

// Generic / unknown H2 → render heading + body as plain markdown
const GenericSectionBlock = ({ heading, body, isFirst }) => {
  // Strip any "N." section-number prefix (with or without pandoc's escape
  // backslash) for cleaner display; the original numbered H2 still
  // serializes via the markdown source if needed for anchor navigation.
  const display = (heading || "").replace(/^\s*\d+(\.\d+)*\\?\.?\s+/, "").trim();
  return (
    <section className={isFirst ? "" : "mt-8"}>
      {heading !== null && (
        <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
          <Pen className="w-3.5 h-3.5 text-slate-500" />
          {display}
        </h2>
      )}
      {body && <Markdown>{body}</Markdown>}
    </section>
  );
};

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const V11ChapterContent = ({ markdown, lang = "en", chapterNum = null }) => {
  const blocks = useMemo(() => splitByH2(markdown), [markdown]);
  if (!blocks.length) return null;

  return (
    <div data-testid="v11-chapter-content" data-chapter-num={chapterNum}>
      {blocks.map((b, i) => {
        const key = `${i}-${b.section_type || "generic"}`;
        switch (b.section_type) {
          case SECTION_TYPE.ABSTRACT:
            return <AbstractBlock key={key} {...b} lang={lang} />;
          case SECTION_TYPE.KEY_EQUATIONS:
            return <KeyEquationsBlock key={key} {...b} lang={lang} />;
          case SECTION_TYPE.WORKED_EXAMPLES:
            return <WorkedExamplesBlock key={key} {...b} lang={lang} chapterNum={chapterNum} />;
          case SECTION_TYPE.SM_CLOSING:
            return <SeniorManagerClosingBlock key={key} {...b} lang={lang} />;
          case SECTION_TYPE.TERMINOLOGY:
            return <TerminologyBlock key={key} {...b} lang={lang} />;
          default:
            return <GenericSectionBlock key={key} {...b} isFirst={i === 0} />;
        }
      })}
    </div>
  );
};

export default V11ChapterContent;
